// PermissionTree.js
import React, { useEffect, useState } from "react";
import {
  Typography,
  Divider,
  Button,
  CircularProgress,
  Box,
  Chip,
  Tooltip,
  Card,
  CardContent,
  CardActions,
  makeStyles,
  FormControlLabel,
  Checkbox,
  Collapse,
  IconButton,
  useTheme
} from "@material-ui/core";
import {
  Save as SaveIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  IndeterminateCheckBox as IndeterminateCheckBoxIcon
} from "@material-ui/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  assignPermissionToRole,
  updateRolesandPermission,
  fetchRolesWithPermissionById,
  fetchRolesWithPermission
} from "../../store/action/permission/permissionAction";

const useStyles = makeStyles((theme) => ({
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    // padding: theme.spacing(1.5, 1.5),
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 200
  },
  sectionTitle: {
    color: theme.palette.primary.main,
    fontWeight: 600,
    marginBottom: 2
  },
  subTitle: {
    color: theme.palette.text.secondary,
    fontSize: "0.85rem",
  },
  treeHeader: {
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    padding: theme.spacing(1),
    borderRadius: theme.spacing(0.5),
    "&:hover": {
      backgroundColor: theme.palette.action.hover
    }
  },
  treeChildren: {
    paddingLeft: theme.spacing(4)
  },
  permissionItem: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.spacing(0.5),
    "&:hover": {
      backgroundColor: theme.palette.action.hover
    }
  },
  moduleHeader: {
    backgroundColor: theme.palette.grey[1000],
    borderRadius: theme.spacing(0.5),
    marginBottom: theme.spacing(1)
  },
  expandButton: {
    marginRight: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  moduleTitle: {
    fontWeight: 600,
    color: theme.palette.listItem,
  },
  permissionChip: {
    fontWeight: 500,
    fontSize: "0.75rem",
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
  },
  permissionList: {
    maxHeight: "71vh",
    overflowY: "auto",
    paddingRight: 8
  }
}));

const PermissionTree = ({ role, cloneRole, selectedPermissions, setSelectedPermissions }) => {
  const theme = useTheme();
  const classes = useStyles();
  const dispatch = useDispatch();

  const [saving, setSaving] = useState(false);
  const [expandedModules, setExpandedModules] = useState({});
  const [hasExistingPermissions, setHasExistingPermissions] = useState(false);

  const { getPermissionItem, loading, fetchRolespermission, roleswithPermissionSuccess } = useSelector(
    (state) => state.permission
  );

  const roleToLoad = role?.id ? role.id : cloneRole.id;
  // Load permissions when role changes
  useEffect(() => {
    if (roleToLoad) {
      dispatch(fetchRolesWithPermissionById(roleToLoad));
    }
  }, [cloneRole, role]);

  // Update selected permissions when API response comes
  useEffect(() => {
    if (fetchRolespermission?.data) {
      // Check if permissions exist in different possible response formats
      const permissions = fetchRolespermission.data.permission || fetchRolespermission.data.permissions || [];

      setSelectedPermissions(permissions);

      // Determine if this role already has permissions
      // This is the key fix - check if there are any permissions assigned
      const hasPermissions = permissions && permissions.length > 0;
      setHasExistingPermissions(hasPermissions);
    }
  }, [fetchRolespermission]);

  // Expand all modules by default when data loads
  useEffect(() => {
    if (getPermissionItem?.data) {
      const expanded = {};
      getPermissionItem.data.forEach((group, index) => {
        expanded[index] = false;
      });
      setExpandedModules(expanded);
    }
  }, [getPermissionItem]);


  const togglePermission = (id) => {
    setSelectedPermissions((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleModule = (index) => {
    setExpandedModules(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const handleSelectAll = (permissionsInGroup) => {
    const ids = permissionsInGroup.map((perm) => {
      const [id] = perm.split(':');
      return Number(id);
    });

    setSelectedPermissions((prev) => {
      const newSelection = [...prev];
      ids.forEach((id) => {
        if (!newSelection.includes(id)) newSelection.push(id);
      });
      return newSelection;
    });
  };

  const handleDeselectAll = (permissionsInGroup) => {
    const ids = permissionsInGroup.map((perm) => {
      const [id] = perm.split(':');
      return Number(id);
    });

    setSelectedPermissions((prev) =>
      prev.filter((id) => !ids.includes(id))
    );
  };

  const isAllSelected = (permissionsInGroup) => {
    const ids = permissionsInGroup.map((perm) => {
      const [id] = perm.split(':');
      return Number(id);
    });

    return ids.every(id => selectedPermissions.includes(id));
  };

  const isSomeSelected = (permissionsInGroup) => {
    const ids = permissionsInGroup.map((perm) => {
      const [id] = perm.split(':');
      return Number(id);
    });

    return ids.some(id => selectedPermissions.includes(id)) &&
      !isAllSelected(permissionsInGroup);
  };

  const handleSave = async () => {
    if (!role || !role.id) return;

    setSaving(true);
    try {

      if (hasExistingPermissions) {
        // Update existing permissions
        const payload = { role_id: role.id, roles: role.id, permission: selectedPermissions };
        const response = await dispatch(updateRolesandPermission(payload));

        if (!response.error) {
          dispatch(fetchRolesWithPermission());
        }
      } else {
        // Assign new permissions
        const payload = { roles: role.id, permission: selectedPermissions };
        const response = await dispatch(assignPermissionToRole(payload));

        if (!response.error) {
          console.log("response -- update per", response)
          // Refresh the roles with permissions data
          dispatch(fetchRolesWithPermission());
          dispatch(fetchRolesWithPermissionById(role.id)); // Refresh current role
          setHasExistingPermissions(true); // Now we have existing permissions
        }
      }
    } catch (err) {
    } finally {
      setSaving(false); // always reset
    }
  };


  // Function to render permission groups in a tree structure
  const renderPermissionTree = () => {
    if (!getPermissionItem?.data) return null;

    return getPermissionItem.data.map((group, index) => {
      const permissions = group.permissions.split(',');
      const allSelected = isAllSelected(permissions);
      const someSelected = isSomeSelected(permissions);

      return (
        <Card key={index} variant="outlined" className={classes.moduleHeader}>
          <CardContent style={{ padding: 0 }}>
            {/* Module Header */}
            <div className={classes.treeHeader} onClick={() => toggleModule(index)}>
              {/* Expand/Collapse button */}
              <IconButton
                size="small"
                className={classes.expandButton}
                onClick={(e) => { e.stopPropagation(); toggleModule(index); }}
              >
                {expandedModules[index] ? <ExpandMoreIcon /> : <ChevronRightIcon />}
              </IconButton>

              {/* Checkbox + Label */}
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                    indeterminateIcon={<IndeterminateCheckBoxIcon fontSize="small" />}
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={() => {
                      if (allSelected) {
                        handleDeselectAll(permissions);
                      } else {
                        handleSelectAll(permissions);
                      }
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                }
                label={
                  <Typography variant="subtitle1" className={classes.moduleTitle}>
                    {group?.module?.toUpperCase()}
                  </Typography>
                }
              />

              {/* Spacer */}
              <Box flexGrow={1} />

              {/* Permission Count */}
              <Chip
                size="small"
                label={`${permissions.filter((perm) => {
                  const [id] = perm.split(":");
                  return selectedPermissions.includes(Number(id));
                }).length}/${permissions.length} perms`}
                className={classes.permissionChip}
              />
            </div>

            {/* Permission Items */}
            <Collapse in={expandedModules[index]} timeout="auto" unmountOnExit>  {/* in={expandedModules[index]} */}
              <div className={classes.treeChildren}>
                {permissions.map((perm) => {
                  const [id, name] = perm.split(':');
                  const permissionId = Number(id);
                  const isSelected = selectedPermissions.includes(permissionId);

                  return (
                    <div key={permissionId} className={classes.permissionItem}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isSelected}
                            onChange={() => togglePermission(permissionId)}
                            color="primary"
                          />
                        }
                        label={
                          <Typography variant="body2" style={{ color: theme.palette.listItem }}>
                            {name?.toUpperCase()}
                          </Typography>
                        }
                      />
                    </div>
                  );
                })}
              </div>
            </Collapse>
          </CardContent>
        </Card>
      );
    });
  };

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div>
      <Box className={classes.header}>
        <div>
          <Typography variant="h6" className={classes.sectionTitle}>
            Permissions for: {role?.name || cloneRole?.name}
          </Typography>
          <Typography variant="body1" color={theme.palette.listItem}>
            {selectedPermissions.length} permission(s) selected
          </Typography>
        </div>

        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
        >
          {saving ? "Saving..." : "Save Permissions"}
        </Button>
      </Box>

      <Divider style={{ marginBottom: 16 }} />

      {role || cloneRole ? (
        <Box className={classes.permissionList}>{renderPermissionTree()}</Box>
      ) : (
        <Typography variant="body2" color="textSecondary">
          No role selected
        </Typography>
      )}
    </div>
  );
};

export default PermissionTree;